<template>
  <div id="product-creation-container" class="position-relative">
    <section class="p-4">
      <header class="px-2">
        <b-button variant="default" class="bg-white mb-4 back-button d-flex align-items-center"
          @click="handleBackButton">
          <i class="bi bi-arrow-left mr-2" aria-hidden="true"></i> Back
        </b-button>
        <h4 class="mb-0 shadow-none">Edit Product</h4>
      </header>
    </section>
    <section id="form-container" class="bg-white p-4 border-top">
      <ProductBasicDetailsForm origin="update_product" />
      <ProductSpecialDiscountsForm v-if="settings.special_discount_enable"/>
      <ProductSalesChannelForm v-if="isSalesChannelVisible" />
      <ProductImagesForm />
      <ProductCategoryForm origin="update" />
      <ProductVariationsForm v-if="productType === 'variable'" :stored-inventory="storedInventory" />
      <ProductAddOnsFormVue />
    </section>
    <ProductFooter :untouchedForm="untouchedForm" :form="form" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";
import ProductBasicDetailsForm from "@/components/products/product_creation/ProductBasicDetailsForm";
import ProductSalesChannelForm from "@/components/products/product_creation/ProductSalesChannelForm";
import ProductImagesForm from "@/components/products/product_creation/ProductImagesForm";
import ProductCategoryForm from "@/components/products/product_creation/ProductCategoryForm";
import ProductVariationsForm from "@/components/products/product_creation/ProductVariationsForm";
import ProductAddOnsFormVue from "@/components/products/product_creation/ProductAddOnsForm";
import ProductFooter from "@/components/products/product_creation/ProductFooter";
import ProductSpecialDiscountsForm from "@/components/products/product_creation/ProductSpecialDiscountsForm";
import { required, requiredIf, url } from "vuelidate/lib/validators";
import { productsMixins } from "@/mixins/products_mixins";
import { mapFields } from "vuex-map-fields";

export default {
  components: {
    ProductBasicDetailsForm,
    ProductSalesChannelForm,
    ProductImagesForm,
    ProductCategoryForm,
    ProductVariationsForm,
    ProductAddOnsFormVue,
    ProductFooter,
    ProductSpecialDiscountsForm
  },
  mixins: [productsMixins],
  data() {
    return {
      storedInventory: [],
    };
  },
  computed: {
    ...mapState("estoreInventories", {
      estoreErrors: (state) => state.errors,
      productDetails: (state) => state.product_data,
    }),
    ...mapState("products", [
      "basic_details",
      "product_images",
      "category_id",
      "product_inventory",
      "product_on_sale",
      "product_attributes",
      "product_variables",
      "add_ons",
      "errors",
    ]),
    ...mapState("branches", ["branches"]),
    ...mapState("auth", ["enabledTopUps"]),
    ...mapFields("products", ["sales_channel", "enable_special_discount"]),
    ...mapState("estoreSettings", ["settings"]),
    form() {
      return _.merge(
        this.basic_details,
        { product_images: this.product_images },
        { category_id: this.category_id },
        { product_inventory: this.product_inventory },
        { product_on_sale: this.product_on_sale },
        { product_variables: this.product_variables },
        { add_ons: this.add_ons },
        { sales_channel: this.sales_channel },
        { enable_special_discount: this.enable_special_discount }
      );
    },
    formErrors() {
      let errors = {
        name: this.apiErrors.name || null,
        description: this.apiErrors.description || null,
        regular_price: this.apiErrors.regular_price || null,
        product_link: this.apiErrors.product_link || null,
        product_images: this.apiErrors.product_images || null,
        category_id: this.apiErrors.category_id || null,
        product_on_sale: {
          discount_value: () => {
            if (_.has(this.apiErrors, "product_on_sale"))
              return this.apiErrors.product_on_sale.discount_value || null;
            else return;
          },
        },
      };

      return errors;
    },
    productType: {
      get() {
        const { type } = this.$route.params;
        return type;
      },
      set(type) {
        return type;
      },
    },
    page: {
      get() {
        const { page } = this.$route.params;
        return page;
      },
      set(page) {
        return page;
      },
    },
    isSalesChannelVisible() {
      return this.enabledTopUps.includes("estore") &&
        this.enabledTopUps.includes("table_ordering");
    },
  },
  validations: {
    form: {
      name: {
        required,
      },
      description: {
        required,
      },
      regular_price: {
        required: requiredIf((form) => form.product_variables.length === 0),
      },
      sku: {
        required: requiredIf((form) => form.product_variables.length === 0),
      },
      product_link: {
        url,
      },
      product_images: {
        required,
      },
      category_id: {
        required,
      },
      product_on_sale: {
        discount_value: {
          required: requiredIf((form) => form.on_sale),
        },
      },
      product_variables: {
        $each: {
          variable: {
            sku: {
              required,
            },
            regular_price: {
              required,
            },
          },
        },
      },
    },
  },
  mounted() {
    this.$root.$on("validateProductCreation", () => {
      this.$v.form.$touch();

      if (!this.$v.form.$invalid) {
        this.$store.commit("products/setErrors", null);
        this.openSavePrompt();
      } else this.setFormErrors();
    });
    this.$root.$on("saveProductCreation", () => this.saveProduct());
    this.$root.$on("updateVariables", () => this.updateVariables());
    this.$root.$on("productAddOnsUpdate", () => {
      this.$forceUpdate();

      this.form.add_ons = this.add_ons;
    });
  },
  beforeDestroy() {
    this.$root.$off("saveProductCreation");
    this.$root.$off("updateVariables");
    this.$store.commit("products/resetForm");
    this.$store.commit("products/setErrors", null);
    this.storedInventory = [];
    this.untouchedForm = {};
    this.$root.$off("productAddOnsUpdate");
    this.$root.$off("validateProductCreation");
  },
  async created() {
    await this.$store.dispatch("auth/getTopUps");
    this.sales_channel.is_available_on_estore = false;
    this.sales_channel.is_available_on_dine_in = false;

    const { params } = this.$route;
    await this.$store
      .dispatch("estoreInventories/getProduct", {
        uuid: params.uuid,
        type: params.type,
      })
      .then(() => {
        if (!_.isEmpty(this.estoreErrors))
          this.$router.push({ name: "Products" });
        else this.setProductDetails();
      });
  },
  methods: {
    setFormErrors() {
      let errors = {
        name: null,
        description: null,
        regular_price: null,
        product_link: null,
        product_images: null,
        category_id: null,
        product_on_sale: {
          discount_value: null,
        },
      };

      const validations = {
        name: ["required"],
        description: ["required"],
        regular_price: ["required"],
        product_link: ["url"],
        product_images: ["required"],
        category_id: ["required"],
        product_on_sale: {
          discount_value: ["required"],
        },
      };

      const validation_messages = {
        required: "This field is required.",
        invalid_format: "Invalid format.",
      };

      Object.keys(validations).forEach((formKey) => {
        if (validations[formKey].length) {
          validations[formKey].forEach((validationKey) => {
            if (
              this.isDirtyAndInvalid(formKey, validationKey) &&
              validationKey === "required"
            )
              errors[formKey] = validation_messages[validationKey];
          });
        } else {
          Object.keys(validations[formKey]).forEach((subFormKey) => {
            validations[formKey][subFormKey].forEach((validationKey) => {
              if (
                this.isDirtyAndInvalid(formKey, validationKey, subFormKey) &&
                validationKey === "required"
              )
                errors[formKey][subFormKey] =
                  validation_messages[validationKey];
            });
          });
        }
      });

      this.$store.commit("products/setErrors", errors);
    },
    openSavePrompt() {
      this.$root.$emit("bv::show::modal", "product-creation-prompt", {
        title: "Update Product?",
        message: "Are you sure you want to save changes to this product?",
        buttons: [
          {
            type: "close",
            label: "Cancel",
          },
          {
            type: "save",
            label: "Yes",
            variant: "primary",
          },
        ],
      });
    },
    setProductDetails() {
      const productData = this.productDetails;
      const { type } = this.$route.params;

      const product = {
        basic_details: {
          name: this.productInfo("productName"),
          description: this.productInfo("description"),
          branch_uuids: productData.branch_uuids
            ? productData.branch_uuids
            : [],
          selected_all_brands_branches:
            productData.selected_all_brands_branches,
          visibility: this.productInfo("visibility"),
          regular_price: productData.regular_price,
          currency: productData.currency,
          sku: productData.sku,
          product_link: null,
        },
        product_images: this.productInfo("productImages"),
        category_id: this.productInfo("categoryId"),
        product_inventory: {
          stock: productData.stock,
          is_limited_stock: productData.is_limited_stock,
        },
        product_on_sale: {
          on_sale: productData.on_sale,
          discount_value: productData.discount_type,
          discount_type: productData.discount_value,
          on_sale_from: productData.on_sale_from
            ? productData.on_sale_from
            : "YYYY-MM-DD HH:MM:SS",
          on_sale_to: productData.on_sale_to
            ? productData.on_sale_to
            : "YYYY-MM-DD HH:MM:SS",
          date_range: null,
        },
        add_ons: this.productInfo("add_ons"),
      };

      this.enable_special_discount = 
        type == "variable" 
          ? (productData.enable_special_discount == 1 
              ? true 
              : false) 
          : (productData.product_details.enable_special_discount == 1 
              ? true 
              : false);      

      if (type === "variable") {
        product["product_attributes"] = productData.attributes.map((attr) => ({
          action: "update",
          name: attr.name,
          uuid: attr.uuid,
          variations: attr.variations.map((variant) => ({
            action: "update",
            name: variant.name,
            hash: variant.hash,
          })),
        }));

        this.storedInventory = JSON.parse(
          JSON.stringify(productData.inventory)
        );
      }

      this.sales_channel = {
        is_available_on_dine_in: Boolean(productData.is_available_on_dine_in),
        is_available_on_estore: Boolean(productData.is_available_on_estore)
      };

      Object.keys(product).forEach((typeKey) => {
        this.$store.commit("products/setProductData", {
          data: product[typeKey],
          type: typeKey,
        });
      });

      this.$nextTick(() => {
        this.untouchedForm = JSON.stringify(this.form);
      });
    },
    updateVariables() {
      let productData = [];

      const existing_product_variables = _.uniq(
        this.product_attributes
          .map((item) => item.variations.map((variant) => variant.hash))
          .flat()
      );

      if (
        this.productDetails.attributes.length === this.product_attributes.length
      )
        productData = _.merge(
          this.productDetails.inventory,
          this.product_variables
        );
      else productData = _.merge(this.product_variables);

      const set_variables = productData
        .map((item) => {
          const existing = this.setProductVariablesItemData(item)
            .variation_code.split("_")
            .every((hash) => existing_product_variables.includes(hash));

          if (existing)
            return {
              action: this.setProductVariablesItemData(item).action,
              variable: {
                variation_code:
                  this.setProductVariablesItemData(item).variation_code,
                stock: this.setProductVariablesItemData(item).stock
                  ? this.setProductVariablesItemData(item).stock
                  : 1,
                currency: this.setProductVariablesItemData(item).currency,
                visibility: this.setProductVariablesItemData(item).visibility,
                sku: this.setProductVariablesItemData(item).sku,
                regular_price:
                  this.setProductVariablesItemData(item).regular_price,
                on_sale: Boolean(this.setProductVariablesItemData(item).on_sale),
                is_limited_stock:
                  this.setProductVariablesItemData(item).is_limited_stock,
                uuid: this.setProductVariablesItemData(item).uuid,
              },
            };
        })
        .filter((item) =>
          _.flatMap(this.storedInventory, "variation_code").includes(
            item.variable.variation_code
          )
        );

      this.$store.commit("products/setProductData", {
        data: _.uniqBy(set_variables, "variable.variation_code"),
        type: "product_variables",
      });

      this.$root.$emit("setProductDataStatus", 1);
    },
    async saveProduct() {
      await this.$store.dispatch("auth/getTopUps");

      const form = this.form;

      if (!this.isSalesChannelVisible) {
        this.sales_channel.is_available_on_estore = true;
        this.sales_channel.is_available_on_dine_in = false;
      }

      const payload = {
        uuid: this.$route.params.uuid,
        updates: {
          action: "update",
          name: form.name,
          description: form.description,
          category_id: form.category_id,
          product_images: form.product_images
            .filter((image) => image.action !== "update")
            .map((image) => ({
              action: image.action,
              uuid: image.uuid ? image.uuid : null,
              image:
                image.action === "delete"
                  ? null
                  : image.image.split("data:image/png;base64,").pop(),
            })),
          is_limited_stock: form.product_inventory.is_limited_stock,
          add_ons: this.saveTransformer("add_ons"),
          branch_uuids: this.branches.length === 1
            ? [this.branches[0].uuid]
            : form.branch_uuids,
          selected_all_brands_branches: form.selected_all_brands_branches,
          on_sale: !!form.product_on_sale.on_sale,
          visibility: form.visibility,
          stock: form.product_inventory.is_limited_stock
            ? form.product_inventory.stock
            : 1,
           enable_special_discount: form.enable_special_discount,
          ...this.sales_channel
        },
      };

      if (payload.updates.product_images.length === 0)
        delete payload.updates.product_images;

      if (form.product_variables.length === 0) {
        payload.updates["currency"] = "PHP";
        payload.updates["regular_price"] = form.regular_price;
        payload.updates["sku"] = form.sku;
        console.log("simple", payload)
        this.$nextTick(async () => {
          await this.$store
            .dispatch("estoreInventories/updateSimpleProduct", payload)
            .then(() => {
              this.$root.$emit("bv::hide::modal", "product-creation-prompt");
            });

          if (_.isEmpty(this.errors) && _.isEmpty(this.estoreErrors))
            this.$router.push({ name: "Products", params: {page: this.page}});
        });
      } else {
        let varcodes = [];
        payload.updates["product_variables"] = form.product_variables.map(
          (item) => {
            _.assign(item.variable, {
              on_sale: !!item.variable.on_sale,
              regular_price: Number(item.variable.regular_price),
            });
            if (!varcodes.includes(item.variable.variation_code)) {
              varcodes.push(item.variable.variation_code);
              return item;
            }
            return;
          }
        );
        payload.updates["product_variables"] = payload.updates["product_variables"].filter((el) => { return el != null });
        payload.updates["product_attributes"] = this.product_attributes;
        payload.updates["deleted_variations"] = this.storedInventory
          .filter(
            (item) =>
              !_.flatMap(
                payload.updates["product_variables"],
                "variable.variation_code"
              ).includes(item.variation_code)
          )
          .map((item) => ({
            action: "delete",
            uuid: item.uuid,
            variation_code: item.variation_code,
          }));
          console.log("vriable",payload)
        this.$nextTick(async () => {
          await this.$store
            .dispatch("estoreInventories/updateVariableProduct", payload)
            .then(() => {
              this.$root.$emit("bv::hide::modal", "product-creation-prompt");
            });

          if (_.isEmpty(this.errors) && _.isEmpty(this.estoreErrors))
            this.$router.push({ name: "Products" });
        });
      }
    },
  },
  watch: {
    add_ons(value) {
      this.form.add_ons = value;
    }
  }
};
</script>

<style lang="scss">
@import url("../../assets/scss/products/_form.scss");
</style>

<style lang="scss" scoped>
#product-creation-container {
  padding-bottom: 87px;
}

header {
  .back-button {
    border: solid 1px #e9ebed;
    color: #2f3941;
    font-size: 14px;
    font-family: MainFontBold, sans-serif;
    font-weight: normal;
    border-radius: 20px;
    box-shadow: 0.25rem 0.25rem 0.55rem rgba(0, 0, 0, 0.15);
  }

  .bi {
    font-size: 18px !important;
  }

  h4 {
    font-size: 20px;
    font-family: MainFontBold, sans-serif;
    font-weight: normal;
    color: #2f3941;
  }
}
</style>
